import dayjs from 'dayjs';
import { z } from 'zod';

export const DynamicEndpointsType = {
  Git: '11',
  GitEncrypt: '12',
  S3: '21',
  S3Encrypt: '22',
} as const;

export const EnvironmentType = {
  LOCAL: 'local',
  DEV: 'dev',
  UAT: 'uat',
  PROD: 'prod',
} as const;

export const dynamicEndpointScheme = z.object({
  source: z.string().url(),
  token: z.string(),
  type: z.nativeEnum(DynamicEndpointsType).default(DynamicEndpointsType.S3),
});

export type DynamicEndpoint = z.infer<typeof dynamicEndpointScheme>;

export type EnvRemoveConfigType = Pick<
  EnvConfigType,
  'defaultEndpointUrl' | 'defaultFileEndpointUrl' | 'defaultWebSocketEndpointUrl'
>;

export const runtimeEndpointSchema = z.object({
  API_ENDPOINT: z
    .string()
    .url()
    .transform((v) => (v.endsWith('/Api') ? v : `${v}/Api`)),
  WEBSOCKET_ENDPOINT: z.string().startsWith('ws'),
  FILE_ENDPOINT: z.string().url(),
  FILEUPLOAD_ENDPOINT: z
    .string()
    .url()
    .transform((v) => (v.endsWith('/Api') ? v : `${v}/Api`)),
});

export type RuntimeEndpointType = z.infer<typeof runtimeEndpointSchema>;

export const runtimeEndpointsSchema = z.object({
  API_ENDPOINTS: z.array(
    z
      .string()
      .url()
      .transform((v) => (v.endsWith('/Api') ? v : `${v}/Api`)),
  ),
  WEBSOCKET_ENDPOINTS: z.array(z.string().startsWith('ws')),
  FILE_ENDPOINTS: z.array(z.string().url()),
  FILEUPLOAD_ENDPOINTS: z.array(
    z
      .string()
      .url()
      .transform((v) => (v.endsWith('/Api') ? v : `${v}/Api`)),
  ),
});

export type RuntimeEndpointsType = z.infer<typeof runtimeEndpointsSchema>;

export const sentrySettingsSchema = z.object({
  dsn: z.string(),
  enable: z.boolean(),
  release: z.string(),
  environment: z.string().nullable(),
});

export const brandKeySchema = z.enum(['ryuryu', 'wangyou', 'kbar']);

export const brandThemeSchema = z.enum(['ryuryu', 'wangyou', 'kbar', 'threetwoone', 'eightk', 'bonglan']);
export const brandSchema = z.object({
  key: brandKeySchema,
  theme: brandThemeSchema,
});

export type BrandKey = z.infer<typeof brandKeySchema>;
export type BrandTheme = z.infer<typeof brandThemeSchema>;

export const configSchema = z
  .object({
    messageRetentionTime: z.string().transform((val) => dayjs.duration(`p${val}`.toUpperCase()).asMilliseconds()),
    logRetentionTime: z.string().transform((val) => dayjs.duration(`p${val}`.toUpperCase()).asMilliseconds()),
    messageLineClamp: z.number().or(z.string()).pipe(z.coerce.number()),
    maxTagMemberCount: z.number(),
    maxForwardSessionCount: z.number(),
    maxForwardMessageCount: z.number(),
    audioMaxDuration: z.number(),
  })
  .partial()
  .nullish()
  .default({
    messageRetentionTime: '7d',
    logRetentionTime: '3d',
    messageLineClamp: 18,
    audioMaxDuration: 60,
    maxTagMemberCount: 500,
    maxForwardSessionCount: 500,
    maxForwardMessageCount: 100,
  });

export const envConfigScheme = z.object({
  env: z.nativeEnum(EnvironmentType).default(EnvironmentType.LOCAL),
  brand: brandSchema.nullish().catch(null),
  config: configSchema,
  sentry: sentrySettingsSchema.passthrough(),

  defaultEndpointUrl: z.array(z.string().url()),
  defaultFileEndpointUrl: z.array(z.string().url()),
  defaultFileUploadEndpointUrl: z.array(z.string().url()),
  defaultWebSocketEndpointUrl: z.array(z.string().startsWith('ws')),
  dynamicEndpointExternalUrl: z.any(), // NOTE: @deprecated
  dynamicEndpoints: z.array(dynamicEndpointScheme).default([]),
});

export type EnvConfigType = z.infer<typeof envConfigScheme>;
